import { Container, Sprite, Texture } from '../../../odie';

import {
  BlockId,
  BlockOptionDef,
  BlockProps,
  blockPropsMap,
} from '../../../game/defs/block';
import { config } from '../../../game/defs/config';
import { BlockDef, CellDef, GridDef } from '../../../game/defs/map';
import { arrayCreate2, arrayIterate2 } from '../../../lib/util/jsTools';
import { createBlockView } from '../../support/blockViewFactory';
import { EditorGridLayer } from './EditorGridLayer';
import { applyEditorCellLayout } from '../../support/editor.blockView.utils';
import { blockOptionToCountSize } from '../../../game/util/blockOption';
import { expandEditorGridDef } from './editorGrid.utils';

// types
//-----------------------------------------------------------------------------
type CellTapListener = (column: number, row: number) => void;

export type EditorGridOptions = {};

type OriginData = {
  column: number;
  row: number;
  width: number;
  height: number;
};

type CellOriginData = { base: OriginData | null; overlay: OriginData | null };
type CellOriginDataMap = CellOriginData[][];
type CellLayer = 'base' | 'overlay';

// constants
//-----------------------------------------------------------------------------
const manifest = {
  guide: 'tile.edit.guide.png',
  center: 'tile.edit.center.png',
};

/*
    collection screen header
*/
export class EditorGrid extends Container {
  // fields
  //-------------------------------------------------------------------------
  // events
  public onCellTap?: CellTapListener;
  // state
  private _gridDef!: GridDef;
  private _dataMap: CellOriginDataMap = [];
  // scene
  private _tileLayer!: EditorGridLayer;
  private _blockLayer1!: EditorGridLayer;
  private _blockLayer2!: EditorGridLayer;
  // textures
  private _textureGuide: Texture;
  private _textureTile: Texture;
  private _pressing = false;
  private _startRef: { column: number; row: number } | null = null;

  // properties
  //-------------------------------------------------------------------------
  // mapDef
  public get grid(): GridDef {
    return this._gridDef;
  }

  public set grid(gridDef: GridDef) {
    // Expand loaded grids to the editor dimensions without changing their enabled area.
    this._gridDef = expandEditorGridDef(gridDef);

    // update data map used by the editor
    this._updateBlockDataMap();

    // update map layers
    this._updateMapLayers();

    // update map cell layouts if needed, ie. for chain and pond blocks
    this._updateMapCellLayouts();
  }

  public clear() {
    this._initDefaultMapDef(true);
    this._updateBlockDataMap();
    this._updateMapLayers();
    this._updateMapCellLayouts();
  }

  // init
  //-------------------------------------------------------------------------
  constructor() {
    super();

    // create textures
    this._textureGuide = Texture.from(manifest.guide);
    this._textureTile = Texture.from(manifest.center);

    // spawn tile layer
    this._spawnTileLayer();

    // spawn guide and input layer
    this._spawnGuideAndInputLayer();

    // spawn block layer 1
    this._spawnBlockLayer1();

    // spawn block layer 1
    this._spawnBlockLayer2();

    // Initialize an empty grid until a level is loaded.
    this._initDefaultMapDef(false);
  }

  public static assets(): string[] {
    return [...Object.values(manifest)];
  }

  // api
  //-------------------------------------------------------------------------
  public placeBlock(column: number, row: number, id: BlockId, def?: BlockDef) {
    const props = blockPropsMap[id];
    const optionDef = this.getBlockOptionDef(id, def);
    const layer = props.overlay ? 'overlay' : 'base';
    const width = optionDef?.width ?? props.width ?? 1;
    const height = optionDef?.height ?? props.height ?? 1;

    // Enable drag placement for blocks that are larger than 1x1
    if (this._startRef) {
      const dx = column - this._startRef.column;
      const dy = row - this._startRef.row;
      const fx = Math.floor(dx / width);
      const fy = Math.floor(dy / height);
      column = this._startRef.column + fx * width;
      row = this._startRef.row + fy * height;
    }

    // Check whether the block can be fitted within the grid
    const valid = this._validateBlockPlacement(column, row, width, height);
    if (!valid) return;

    // Clear existing blocks ----------------------------------
    this._clearBlocksInArea(layer, column, row, width, height);

    // Clear overlay blocks when replacing base blocks
    if (layer === 'base') {
      this._clearBlocksInArea('overlay', column, row, width, height);
    }

    // Update grid def ----------------------------------
    const blockDef = def ?? { id };
    const cell = this._getCell(column, row);
    const blocks = cell.blocks ?? [];

    // find existing block matching layer
    const index = blocks.findIndex((def) =>
      layer === 'base'
        ? !blockPropsMap[def.id].overlay
        : blockPropsMap[def.id].overlay,
    );

    // if found, replace, else add
    if (index >= 0) blocks[index] = blockDef;
    else blocks.push(blockDef);

    // overwrite in case of no blocks already defined on cell
    cell.blocks = blocks;

    // enable cell if not already
    cell.enabled = true;

    // Update origin data ----------------------------------
    this._setOriginData(layer, { column, row, width, height });

    // Ensure cells underneath the block are enabled
    this._setCellsEnabled(true, column, row);

    // Refresh cell and layout ----------------------------------
    this._updateMapCell(column, row);
    this._updateMapCellLayouts();
  }

  public clearCell(column: number, row: number, disable = false) {
    this._setCellsEnabled(!disable, column, row);
    this.clearBlock('base', column, row);
    this.clearBlock('overlay', column, row);
  }

  public clearBlock(layer: CellLayer, column: number, row: number) {
    this._clearBlocksInArea(layer, column, row, 1, 1);
  }

  // private: init
  //-------------------------------------------------------------------------
  private _initDefaultMapDef(cellsEnabled: boolean) {
    const { columns, rows } = config.map;

    // create default cells
    const cells = arrayCreate2<CellDef>(columns, rows, () => ({
      enabled: cellsEnabled,
    })); // set default map def

    // Store the grid directly because it already matches the editor dimensions.
    this._gridDef = {
      columns,
      rows,
      cells,
    };

    // Size the container to the complete editable grid.
    this.width = columns * config.tile.size;
    this.height = rows * config.tile.size;
  }

  // private: updaters
  //-------------------------------------------------------------------------
  private _updateBlockDataMap() {
    this._dataMap.length = 0;

    for (let i = 0; i < this._gridDef.columns; i++) {
      let row = [];
      for (let j = 0; j < this._gridDef.rows; j++) {
        row.push({ base: null, overlay: null });
      }
      this._dataMap.push(row);
    }

    // iterate map def and update all multi-size origins
    arrayIterate2(this._gridDef.cells, (def, column, row) => {
      const base = this._getCellBlockSize('base', column, row);
      const overlay = this._getCellBlockSize('overlay', column, row);
      if (base) this._setOriginData('base', { column, row, ...base });
      if (overlay) this._setOriginData('overlay', { column, row, ...overlay });
    });
  }

  private _updateMapLayers() {
    // iterate map def and update all grid cells
    arrayIterate2(this._gridDef.cells, (_, column: number, row: number) => {
      this._updateMapCell(column, row);
    });
  }

  private _updateMapCellLayouts() {
    // iterate map def and update all grid cells
    arrayIterate2(this._gridDef.cells, (cell, column, row) => {
      cell.blocks?.forEach((blockDef) => {
        const props = blockPropsMap[blockDef.id];
        const cell = props.overlay
          ? this._blockLayer2.getCell(column, row)
          : this._blockLayer1.getCell(column, row);
        const view = cell?.view;
        if (view)
          applyEditorCellLayout(this._gridDef, blockDef, view, column, row);
      });
    });
  }

  private _validateBlockPlacement(
    column: number,
    row: number,
    width: number,
    height: number,
  ) {
    return (
      row >= 0 &&
      column >= 0 &&
      row + height <= this._gridDef.rows &&
      column + width <= this._gridDef.columns
    );
  }

  /**
   * set origin reference for all occupied cells
   */
  private _setOriginData(layer: CellLayer, origin: OriginData) {
    const { column, row, width, height } = origin;
    for (let i = 0; i < width; i++) {
      for (let j = 0; j < height; j++) {
        this._dataMap[column + i][row + j][layer] = {
          column,
          row,
          width,
          height,
        };
      }
    }
  }

  /**
   * get the size of the block at the supplied cell coordinate on the supplied layer
   */
  private _getCellBlockSize(layer: CellLayer, column: number, row: number) {
    const cell = this._getCell(column, row);
    const blocks = cell.blocks ?? [];
    const index = blocks?.findIndex((def) =>
      layer === 'base'
        ? !blockPropsMap[def.id].overlay
        : blockPropsMap[def.id].overlay,
    );

    if (index >= 0) {
      const blockDef = blocks[index];
      const id = blockDef.id;
      const props = blockPropsMap[id];
      let width = props.width ?? 1;
      let height = props.height ?? 1;

      if (props.optionType === 'countSize') {
        if (blockDef?.option !== undefined) {
          const countSize = blockOptionToCountSize(blockDef.option);
          width = countSize?.width ?? props.width ?? 1;
          height = countSize?.height ?? props.height ?? 1;
        }
      }

      return { width, height };
    }
    return null;
  }

  /**
   * clear the block at the supplied origin
   */
  private _clearBlockByOrigin(layer: CellLayer, origin: OriginData) {
    const { column, row, width, height } = origin;

    // Clear cell def
    const cell = this._getCell(column, row);
    const blocks = cell.blocks ?? [];

    const index = blocks?.findIndex((def) =>
      layer === 'base'
        ? !blockPropsMap[def.id].overlay
        : blockPropsMap[def.id].overlay,
    );

    if (index >= 0) blocks?.splice(index, 1);

    // Clear origin data
    for (let i = 0; i < width; i++) {
      for (let j = 0; j < height; j++) {
        this._dataMap[column + i][row + j][layer] = null;
        this._updateMapCell(column + i, row + j);
      }
    }

    // Refresh cell layouts
    this._updateMapCellLayouts();
  }

  /**
   * clear all the blocks that lie within the supplied area
   */
  private _clearBlocksInArea(
    layer: CellLayer,
    column: number,
    row: number,
    width: number,
    height: number,
  ) {
    const origins: OriginData[] = [];
    for (let i = 0; i < width; i++) {
      for (let j = 0; j < height; j++) {
        const origin = this._getOrigin(layer, column + i, row + j);
        if (!origin) continue;
        const existed = origins.find(
          (org) => org.column === origin.column && org.row === origin.row,
        );
        if (!existed) origins.push(origin);
      }
    }
    origins.forEach((origin) => this._clearBlockByOrigin(layer, origin));
  }

  /**
   * set the enabled state of all cells that are under the same block
   * at the supplied cell coordinate, if any
   */
  private _setCellsEnabled(enabled: boolean, column: number, row: number) {
    // Accounted for only the area occupied by the base layer block,
    // if not then just the supplied cell coordinate
    const origin = this._getOrigin('base', column, row) ?? {
      column,
      row,
      width: 1,
      height: 1,
    };
    for (let i = 0; i < origin.width; i++) {
      for (let j = 0; j < origin.height; j++) {
        const cell = this._getCell(origin.column + i, origin.row + j);
        cell.enabled = enabled;
        this._updateMapCell(origin.column + i, origin.row + j);
      }
    }
  }

  /**
   * get block origin data at the supplied cell coordinate on the supplied layer
   */
  private _getOrigin(layer: CellLayer, column: number, row: number) {
    return this._dataMap[column][row][layer];
  }

  private _getCell(column: number, row: number) {
    return this._gridDef.cells[column][row];
  }

  private _getTileSprite() {
    const sprite = new Sprite(this._textureTile);
    sprite.width = config.tile.size;
    sprite.height = config.tile.size;
    return sprite;
  }

  private _updateMapCell(column: number, row: number) {
    // get map cell def
    const cellDef = this._getCell(column, row);

    // update tile state
    this._tileLayer.setCell(
      column,
      row,
      cellDef.enabled ? this._getTileSprite() : undefined,
    );

    // clear existing layers
    this._blockLayer1.setCell(column, row);
    this._blockLayer2.setCell(column, row);

    // if enabled and have blocks
    if (cellDef.enabled && cellDef.blocks) {
      // for each block def
      for (const blockDef of cellDef.blocks) {
        // choose block layer
        const blockLayer = blockPropsMap[blockDef.id].overlay
          ? this._blockLayer2
          : this._blockLayer1;

        // create block view
        const blockView = createBlockView(blockDef);

        // add block view to layer grid
        blockLayer.setCell(column, row, blockView);
      }
    }
  }

  // private: spawn
  //-------------------------------------------------------------------------
  private _spawnTileLayer() {
    const layer = (this._tileLayer = new EditorGridLayer());
    this.addChild(layer);
  }

  private _spawnBlockLayer1() {
    const layer = (this._blockLayer1 = new EditorGridLayer());
    this.addChild(layer);
  }

  private _spawnBlockLayer2() {
    const layer = (this._blockLayer2 = new EditorGridLayer());
    this.addChild(layer);
  }

  private _spawnGuideAndInputLayer() {
    // create back layer
    const layer = new EditorGridLayer();

    // create back tiles
    arrayIterate2(layer.cells, (_, column: number, row: number) => {
      // create guide view
      const guideView = new Sprite(this._textureGuide);
      guideView.width = config.tile.size;
      guideView.height = config.tile.size;

      // lower sprite alpha
      guideView.alpha = 0.5;

      // register cell input
      guideView['_innerView'].onInputStart = () => {
        this.startPressing(column, row);
        this.onCellTap?.(column, row);
      };
      guideView['_innerView'].onInputOver = () => {
        if (!this._pressing) return;
        // Trigger cell tap on roll over, if pressing, for 'drawing-like' functionality
        this.onCellTap?.(column, row);
      };

      guideView.interactive = true;

      // add guide view to grid
      layer.setCell(column, row, guideView);
    });

    // spawn
    this.addChild(layer);

    // Make sure to stop pressing on mouse up or touch end
    window.addEventListener('mouseup', () => this.stopPressing());
    window.addEventListener('mouseout', () => this.stopPressing());
    window.addEventListener('mouseleave', () => this.stopPressing());
    window.addEventListener('touchend', () => this.stopPressing());
    window.addEventListener('touchcancel', () => this.stopPressing());
  }

  private startPressing(column: number, row: number) {
    this._pressing = true;
    this._startRef = { column, row };
  }

  private stopPressing() {
    this._pressing = false;
    this._startRef = null;
  }

  private getBlockOptionDef(id: BlockId, def?: BlockDef) {
    const props = blockPropsMap[id];

    // For the case of countSize option type like the curtains
    if (props.optionType === 'countSize') {
      // Return nothing if no option is defined to allow fallback.
      if (def?.option === undefined) return null;
      // Otherwise, return decoded options.
      return blockOptionToCountSize(def.option);
    }

    return props.options?.[def?.option ?? 0] as BlockOptionDef & {
      width?: number;
      height?: number;
    };
  }
}
